import { useTranslation } from "react-i18next"
import type { RodizioSession } from "../../Objects/Rodizio/RodizioSession"
import type { IPublicMenuTheme } from "../Helpers/PublicMenuTheme"

interface Props {
  sessions: RodizioSession[]
  selectedSession: RodizioSession | null
  canJoin: boolean
  theme: IPublicMenuTheme
  isLegacyTheme?: boolean
  onSelect: (session: RodizioSession) => void
  onJoin: () => void
}

export const RodizioPublicPanel: React.FC<Props> = ({
  sessions,
  selectedSession,
  canJoin,
  theme,
  isLegacyTheme = false,
  onSelect,
  onJoin,
}) => {
  const { t } = useTranslation("rodizio")

  return (
    <section
      className={`mb-2 rounded-lg px-3 py-2 shadow-sm ${isLegacyTheme ? "border border-gray-100 bg-white" : "border"}`}
      style={
        isLegacyTheme
          ? undefined
          : {
              borderColor: theme.borderColor,
              borderWidth: theme.borderThickness,
              background: theme.useGradientHeader
                ? `linear-gradient(135deg, ${theme.headerBackgroundColor} 0%, ${theme.surfaceColor} 55%, ${theme.backgroundColor} 100%)`
                : theme.headerBackgroundColor,
              color: theme.textColor,
            }
      }
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <p className="text-[10px] font-black uppercase tracking-wide" style={{ color: theme.primaryColor }}>
            {t("public.badge")}
          </p>

          <h2 className="truncate text-sm font-black uppercase tracking-wide">
            {selectedSession ? t("public.active_for", { name: selectedSession.participantName }) : t("public.title")}
          </h2>

          <p className="mt-0.5 line-clamp-2 text-[11px] font-semibold opacity-75">
            {selectedSession ? t("public.active_description") : t("public.description")}
          </p>
        </div>

        {canJoin && !selectedSession && (
          <button
            type="button"
            onClick={onJoin}
            className="min-h-10 shrink-0 rounded-lg px-4 text-xs font-black uppercase tracking-wide"
            style={{ backgroundColor: theme.buttonColor, color: theme.buttonTextColor }}
          >
            {t("public.enter_code")}
          </button>
        )}
      </div>

      {sessions.length > 0 && (
        <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
          {sessions.map((session) => (
            <button
              key={session.id}
              type="button"
              onClick={() => onSelect(session)}
              aria-pressed={selectedSession?.id === session.id}
              className="min-h-10 shrink-0 rounded-lg border px-3 py-1.5 text-left text-xs font-black"
              style={{
                borderColor: selectedSession?.id === session.id ? theme.primaryColor : theme.borderColor,
                backgroundColor: selectedSession?.id === session.id ? theme.secondaryColor : theme.surfaceColor,
                borderWidth: theme.borderThickness,
              }}
            >
              {session.participantName}
              <span className="ml-2 text-xs opacity-70">{session.planName}</span>
            </button>
          ))}
        </div>
      )}
    </section>
  )
}
